import React, { useEffect, useState } from "react";
import { getDiaryList, IDiaryListResult, toggleDiaryLike } from "api/student/v2/diary.api";
import { IDiary } from "api/interfaces/profile.interface";
import { getImageUrl } from "helpers/Image.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import StudentDiaryDetailPopup from "./popup/StudentDiaryDetailPopup";
import { usePopup } from "contexts/PopupContext";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";

interface StudentDiaryListProps {
    studentId: string | undefined;
    isActiveOfPublicProfile: boolean;
}

const DIARY_LIMIT = 10;

const StudentDiaryList: React.FC<StudentDiaryListProps> = ({ studentId, isActiveOfPublicProfile }) => {
    const { showPopup } = usePopup();
    const [isOnLoading, setIsOnLoading] = useState(false);
    const [diaryData, setDiaryData] = useState<IDiaryListResult | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [likeLoading, setLikeLoading] = useState<{ [key: number]: boolean }>({});

    useEffect(() => {
        if (isActiveOfPublicProfile && studentId) {
            loadDiaries();
        }
    }, [studentId, isActiveOfPublicProfile]);

    const loadDiaries = async (page: number = 1) => {
        if (!studentId) return;

        setIsOnLoading(true);
        try {
            const data = await getDiaryList({ 
                page, 
                limit: DIARY_LIMIT, 
                userStudentId: parseInt(studentId) 
            });
            
            if (data?.diaryList) {
                if (page === 1) {
                    setDiaryData(data);
                } else {
                    setDiaryData(prevData => {
                        if (!prevData) return data;
                        return {
                            ...data,
                            diaryList: [...prevData.diaryList, ...data.diaryList]
                        };
                    });
                }
                setPage(page);
                setHasMore(data.pagination.hasNextPage);
            }
        } catch (error: any) {
            console.error(error);
            setErrorMessage(error.message || "다이어리 목록을 불러오는데 실패했습니다.");
            setHasMore(false);
        } finally {
            setIsOnLoading(false);
        }
    };

    const loadMoreDiaries = () => {
        loadDiaries(page + 1);
    };

    const handleDiaryClick = (diaryId: number) => {
        showPopup(
            <StudentDiaryDetailPopup
                diaryId={diaryId.toString()}
                onClose={() => showPopup(null)}
            />
        );
    };

    const handleImageClick = (images: any[], initialIndex: number = 0) => {
        showPopup(
            <ImageSlidePopup
                imageInfoList={images.map(img => img.image)}
                initialSlideIdx={initialIndex}
                onClose={() => { }}
            />
        );
    };

    // 좋아요 토글 함수
    const handleLikeToggle = async (diaryId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // 이벤트 버블링 방지

        try {
            setLikeLoading(prev => ({ ...prev, [diaryId]: true }));

            const result = await toggleDiaryLike(diaryId.toString());

            // 다이어리 데이터 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;

                return {
                    ...prevData,
                    diaryList: prevData.diaryList.map(diary =>
                        diary.id === diaryId
                            ? {
                                ...diary,
                                isLiked: result.isLiked,
                                likeCount: result.likeCount
                            }
                            : diary
                    )
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setLikeLoading(prev => ({ ...prev, [diaryId]: false }));
        }
    };

    if (!isActiveOfPublicProfile) {
        return null;
    }

    // 다이어리 스켈레톤 컴포넌트
    const DiarySkeleton = () => (
        <div className="space-y-4">
            {[1, 2, 3].map((index) => (
                <div key={`skeleton-${index}`} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                    {/* 다이어리 헤더 스켈레톤 */}
                    <div className="p-4 border-b border-gray-100">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="h-6 bg-gray-200 rounded w-48"></div>
                                <div className="h-5 bg-gray-200 rounded w-16"></div>
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                        </div>
                    </div>

                    {/* 다이어리 내용 스켈레톤 */}
                    <div className="p-4">
                        {/* 내용 스켈레톤 */}
                        <div className="mb-4 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 비디오 스켈레톤 */}
                        <div className="mb-4">
                            <div className="h-48 bg-gray-200 rounded-lg"></div>
                        </div>

                        {/* 이미지 스켈레톤 */}
                        <div className="mb-4">
                            <div className="flex gap-2">
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                            </div>
                        </div>

                        {/* 통계 정보 스켈레톤 */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-1">
                                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </div>
                            </div>
                            <div className="h-8 bg-gray-200 rounded w-20"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="mt-8">
            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
                {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtTitleDiaries") || "다이어리"}
            </h2>

            {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-red-600 font-medium">{errorMessage}</p>
                    </div>
                </div>
            )}

            {/* 로딩 중 스켈레톤 표시 */}
            {isOnLoading && !diaryData && (
                <DiarySkeleton />
            )}

            {diaryData && diaryData.diaryList.length === 0 && !isOnLoading && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-gray-600 font-medium text-center">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtNoDiaries") || "작성된 다이어리가 없습니다."}
                    </p>
                </div>
            )}

            {diaryData && diaryData.diaryList.length > 0 && (
                <div className="space-y-4">
                    {diaryData.diaryList.map((entry: IDiary) => (
                        <div
                            key={entry.id}
                            className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
                            onClick={() => handleDiaryClick(entry.id)}
                        >
                            {/* 다이어리 헤더 */}
                            <div className="p-4 border-b border-gray-100">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-bold text-gray-900 truncate">
                                            {entry.title}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            {entry.isPortfolio && (
                                                <div className="flex items-center gap-1 px-2 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Portfolio
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        {new Date(entry.createdAt).toLocaleDateString('ko-KR')}
                                    </p>
                                </div>
                            </div>

                            {/* 다이어리 내용 */}
                            <div className="p-4">
                                <div className="mb-4">
                                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap line-clamp-3">
                                        {entry.content}
                                    </p>
                                </div>

                                {/* 비디오 */}
                                {entry.diaryVideos && entry.diaryVideos.length > 0 && (
                                    <div className="mb-4">
                                        {entry.diaryVideos.map((diaryVideo) => (
                                            <div key={diaryVideo.id} className="relative">
                                                <VideoPlayer
                                                    video={diaryVideo.video}
                                                    className="w-full rounded-lg border border-gray-200"
                                                    onVideoEnd={() => {}}
                                                    onVideoError={(error) => {
                                                        console.error('비디오 에러:', error);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 이미지 */}
                                {entry.diaryImages && entry.diaryImages.length > 0 && (
                                    <div className="mb-4">
                                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                            {entry.diaryImages.slice(0, 3).map((diaryImage, index) => (
                                                <div
                                                    key={diaryImage.id}
                                                    className="flex-shrink-0 aspect-square w-20 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleImageClick(entry.diaryImages, index);
                                                    }}
                                                >
                                                    <img
                                                        src={getImageUrl(diaryImage.image)}
                                                        alt={`다이어리 이미지 ${diaryImage.sequence}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ))}
                                            {entry.diaryImages.length > 3 && (
                                                <div className="flex-shrink-0 aspect-square w-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                                    <span className="text-xs text-gray-500">
                                                        +{entry.diaryImages.length - 3}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                        {/* 통계 정보 */}
                                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                {/* 좋아요 버튼 */}
                                                <button
                                                    className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                                                    onClick={(e) => handleLikeToggle(entry.id, e)}
                                                    disabled={likeLoading[entry.id]}
                                                >
                                                    {likeLoading[entry.id] ? (
                                                        <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            className={`w-4 h-4 ${entry.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                            fill={entry.isLiked ? '#CF3688' : 'none'}
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                        </svg>
                                                    )}
                                                    <span className={entry.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}>
                                                        {entry.likeCount || 0}
                                                    </span>
                                                </button>

                                        {/* 댓글 수 */}
                                        {entry.commentCount !== undefined && (
                                            <div className="flex items-center space-x-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                <span>{entry.commentCount}</span>
                                            </div>
                                        )}

                                        {/* 북마크 수 */}
                                        <div className="flex items-center space-x-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            <span>{(entry as any).bookmarkCount || 0}</span>
                                        </div>
                                    </div>

                                    {/* 자세히 보기 버튼 */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDiaryClick(entry.id);
                                        }}
                                        className="flex items-center space-x-1 px-3 py-1.5 text-sm text-[#CF3688] hover:text-[#CF3688]/80 hover:bg-[#CF3688]/5 rounded-lg transition-all duration-200 font-medium"
                                    >
                                        <span>{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnViewDetail") || "자세히 보기"}</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* 더보기 버튼 */}
                    {!isOnLoading && hasMore && (
                        <div className="text-center">
                            <button 
                                className="px-4 py-2 text-[#CF3688] border border-[#CF3688] rounded-full text-sm font-medium hover:bg-[#CF3688]/5 transition-colors"
                                onClick={loadMoreDiaries}
                            >
                                {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtMore") || "더보기"}
                            </button>
                        </div>
                    )}

                    {/* 더보기 로딩 인디케이터 */}
                    {isOnLoading && diaryData && diaryData.diaryList.length > 0 && (
                        <div className="flex justify-center items-center py-4">
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-sm text-gray-500">더 많은 다이어리를 불러오는 중...</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StudentDiaryList;
